/* eslint-disable react-hooks/immutability */
import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import maplibregl from "maplibre-gl";
import type { Map as MapInstance, MapOptions } from "maplibre-gl";

import { createBaseMap } from "./createBaseMap";

export type BaseMapStatus = {
  isMapReady: boolean;
  hasRenderedTiles: boolean;
};

type BaseMapOptions = Omit<MapOptions, "container" | "style">;

type UseBaseMapInstanceArgs = {
  style: string;
  mapOptions?: BaseMapOptions;
  onLoad?: (map: MapInstance) => void;
  onStyleData?: (map: MapInstance) => void;
  mapRefOverride?: MutableRefObject<MapInstance | null>;
};

export function useBaseMapInstance({
  style,
  mapOptions,
  onLoad,
  onStyleData,
  mapRefOverride,
}: UseBaseMapInstanceArgs) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const fallbackMapRef = useRef<MapInstance | null>(null);
  const mapRef = mapRefOverride ?? fallbackMapRef;
  const styleRef = useRef<string | null>(null);
  const onLoadRef = useRef(onLoad);
  const onStyleDataRef = useRef(onStyleData);
  const initialOptionsRef = useRef(mapOptions);
  const [status, setStatus] = useState<BaseMapStatus>({
    isMapReady: false,
    hasRenderedTiles: false,
  });

  useEffect(() => {
    onLoadRef.current = onLoad;
  }, [onLoad]);

  useEffect(() => {
    onStyleDataRef.current = onStyleData;
  }, [onStyleData]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || mapRef.current) {
      return;
    }

    const map = createBaseMap(maplibregl, {
      container,
      style,
      ...(initialOptionsRef.current ?? {}),
    });

    mapRef.current = map;
    styleRef.current = style;
    setStatus({ isMapReady: false, hasRenderedTiles: false });

    const handleLoad = () => {
      setStatus({ isMapReady: true, hasRenderedTiles: false });
      onLoadRef.current?.(map);
    };

    const handleIdle = () => {
      setStatus((prev) =>
        prev.isMapReady && !prev.hasRenderedTiles
          ? { ...prev, hasRenderedTiles: true }
          : prev,
      );
    };

    map.on("load", handleLoad);
    map.on("idle", handleIdle);

    return () => {
      map.off("load", handleLoad);
      map.off("idle", handleIdle);
      map.remove();
      mapRef.current = null;
      styleRef.current = null;
    };
  }, [mapRef, style]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }
    if (styleRef.current === style) {
      return;
    }
    styleRef.current = style;
    setStatus((prev) => ({ ...prev, isMapReady: false, hasRenderedTiles: false }));
    map.setStyle(style);
    map.once("styledata", () => {
      onStyleDataRef.current?.(map);
      setStatus((prev) => ({ ...prev, isMapReady: true, hasRenderedTiles: false }));
      map.once("idle", () => {
        setStatus((prevStatus) =>
          prevStatus.isMapReady && !prevStatus.hasRenderedTiles
            ? { ...prevStatus, hasRenderedTiles: true }
            : prevStatus,
        );
      });
    });
  }, [mapRef, style]);

  const resize = useCallback(() => {
    mapRef.current?.resize();
  }, [mapRef]);

  return {
    containerRef,
    mapRef,
    status,
    resize,
  };
}
